import React, { useEffect, useRef, useState } from "react";
import { Download, Share2 } from "lucide-react";
import { TEAMS } from "../lib/game";

const YELLOW = "#ffda00";
const RED = "#e60808";
const DEEPEST = "#3a1d04";
const DARK = "#5d3b00";

const isMobileDevice = () => {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// Draw an image with hard offset drop shadow (simulating CSS drop-shadow(Xpx Ypx 0 color))
// We draw a tinted silhouette behind the image by using the image itself as a mask
// on an offscreen canvas, then blitting that onto the main canvas.
const drawImageWithHardShadow = (ctx, img, x, y, w, h, offX, offY, shadowColor) => {
  const off = document.createElement("canvas");
  off.width = Math.ceil(w) + Math.abs(offX) + 4;
  off.height = Math.ceil(h) + Math.abs(offY) + 4;
  const octx = off.getContext("2d");
  // Silhouette: draw the image, then tint by source-in
  octx.drawImage(img, 0, 0, w, h);
  octx.globalCompositeOperation = "source-in";
  octx.fillStyle = shadowColor;
  octx.fillRect(0, 0, off.width, off.height);

  ctx.drawImage(off, x + offX, y + offY);
  ctx.drawImage(img, x, y, w, h);
};

// Filled rectangle with a hard offset shadow behind
const drawBoxWithShadow = (ctx, x, y, w, h, fill, stroke, strokeW, offX, offY, shadowColor) => {
  ctx.fillStyle = shadowColor;
  ctx.fillRect(x + offX, y + offY, w, h);
  ctx.fillStyle = fill;
  ctx.fillRect(x, y, w, h);
  if (stroke && strokeW) {
    ctx.lineWidth = strokeW;
    ctx.strokeStyle = stroke;
    ctx.strokeRect(x + strokeW / 2, y + strokeW / 2, w - strokeW, h - strokeW);
  }
};

// Wrap text to a maximum width, returning the lines.
const wrapText = (ctx, text, maxWidth) => {
  const words = String(text).split(/\s+/);
  const lines = [];
  let current = "";
  for (const word of words) {
    const test = current ? current + " " + word : word;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
};

const VictoryCard = ({ username, playerTeam = "chicken", won, foeLabel, score, bestMs, avgMs }) => {
  const previewRef = useRef(null);
  const wrapperRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [imgs, setImgs] = useState(null);
  const [scale, setScale] = useState(1);

  const enemyTeam = playerTeam === "beef" ? "chicken" : "beef";
  const playerSrc = playerTeam === "beef" ? "/brand/cup-beef.png" : "/brand/cup-chicken.png";
  const enemySrc = enemyTeam === "beef" ? "/brand/cup-beef.png" : "/brand/cup-chicken.png";

  // Scale the preview to fit its parent container so mobile users see the full card.
  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(1, w / 760));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      loadImage("/brand/lockup.png"),
      loadImage(playerSrc),
      loadImage(enemySrc),
    ])
      .then(([lockup, player, enemy]) => {
        if (!cancelled) setImgs({ lockup, player, enemy });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [playerSrc, enemySrc]);

  const renderToCanvas = async () => {
    if (!imgs) return null;
    // Ensure fonts are ready so canvas text uses correct faces on mobile.
    if (document.fonts && document.fonts.load) {
      try {
        await Promise.all([
          document.fonts.load("italic 700 34px Anton"),
          document.fonts.load("700 24px Anton"),
          document.fonts.load("700 20px Anton"),
          document.fonts.load("700 16px Anton"),
          document.fonts.load("14px 'Special Elite'"),
          document.fonts.load("13px 'Special Elite'"),
          document.fonts.load("11px 'Special Elite'"),
          document.fonts.load("9px 'Special Elite'"),
        ]);
        if (document.fonts.ready) await document.fonts.ready;
      } catch (e) { /* non-fatal */ }
    }

    const W = 760;
    const H = 506;
    const dpr = 2;
    const canvas = document.createElement("canvas");
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.textBaseline = "alphabetic";

    // Background
    ctx.fillStyle = YELLOW;
    ctx.fillRect(0, 0, W, H);

    // Outer drop shadow (behind the card — not visible since we are the card)
    // Outer border
    ctx.lineWidth = 4;
    ctx.strokeStyle = DEEPEST;
    ctx.strokeRect(2, 2, W - 4, H - 4);

    // Lockup centered top (~52% width)
    const { lockup, player, enemy } = imgs;
    const lockupW = W * 0.52;
    const lockupH = (lockup.naturalHeight / lockup.naturalWidth) * lockupW;
    const lockupX = (W - lockupW) / 2;
    const lockupY = 12;
    drawImageWithHardShadow(ctx, lockup, lockupX, lockupY, lockupW, lockupH, 4, 5, "rgba(58,29,4,0.35)");

    // Player cup bottom-left (height 200)
    const cupH = 200;
    const playerW = (player.naturalWidth / player.naturalHeight) * cupH;
    const playerX = 12;
    const playerY = H - cupH - 56;
    drawImageWithHardShadow(ctx, player, playerX, playerY, playerW, cupH, 4, 5, "rgba(58,29,4,0.4)");

    // Enemy cup bottom-right
    const enemyW = (enemy.naturalWidth / enemy.naturalHeight) * cupH;
    const enemyX = W - enemyW - 12;
    const enemyY = H - cupH - 56;
    drawImageWithHardShadow(ctx, enemy, enemyX, enemyY, enemyW, cupH, 4, 5, "rgba(58,29,4,0.4)");

    // Stat boxes (5 boxes, 80 wide x 72 tall, gap 6, bottom 92)
    const statW = 80;
    const statH = 72;
    const statGap = 6;
    const stats = [
      { label: "PARTNER", value: TEAMS[playerTeam].label.replace("Team ", "") },
      { label: "FOE", value: String(foeLabel) },
      { label: "SCORE", value: String(score) },
      { label: "BEST", value: bestMs ? `${bestMs}ms` : "—" },
      { label: "AVG", value: avgMs ? `${avgMs}ms` : "—" },
    ];
    const totalW = stats.length * statW + (stats.length - 1) * statGap;
    let sx = (W - totalW) / 2;
    const sy = H - 92 - statH;
    for (const s of stats) {
      drawBoxWithShadow(ctx, sx, sy, statW, statH, "#ffffff", DEEPEST, 3, 3, 3, "rgba(58,29,4,0.45)");
      // label
      ctx.fillStyle = DARK;
      ctx.font = "9px 'Special Elite', monospace";
      ctx.textAlign = "center";
      ctx.fillText(s.label, sx + statW / 2, sy + 18);
      // value (auto-shrink and wrap to fit)
      ctx.fillStyle = DEEPEST;
      const maxW = statW - 10;
      const candidateSizes = [22, 20, 18, 16, 14, 12, 10];
      let chosen = candidateSizes[0];
      let lines = [];
      for (const size of candidateSizes) {
        ctx.font = `700 ${size}px Anton, sans-serif`;
        lines = wrapText(ctx, s.value, maxW);
        const tooWide = lines.some((l) => ctx.measureText(l).width > maxW);
        if (!tooWide && lines.length <= 2) { chosen = size; break; }
      }
      ctx.font = `700 ${chosen}px Anton, sans-serif`;
      const lineHeight = chosen * 1.05;
      const blockH = lineHeight * lines.length;
      const textStartY = sy + statH / 2 - blockH / 2 + chosen + 2;
      lines.forEach((line, i) => {
        ctx.fillText(line, sx + statW / 2, textStartY + i * lineHeight);
      });
      sx += statW + statGap;
    }

    // Victory / Defeat banner (rotated -2deg), middle-ish
    const bannerText = won ? "VICTORY!" : "DEFEATED";
    ctx.save();
    const bannerCenterX = W / 2;
    const bannerCenterY = 260;
    ctx.translate(bannerCenterX, bannerCenterY);
    ctx.rotate((-2 * Math.PI) / 180);
    ctx.font = "italic 700 34px Anton, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const textW = ctx.measureText(bannerText).width;
    const padX = 28;
    const padY = 10;
    const bw = textW + padX * 2;
    const bh = 34 + padY * 2;
    // shadow
    ctx.fillStyle = "rgba(58,29,4,0.5)";
    ctx.fillRect(-bw / 2 + 5, -bh / 2 + 5, bw, bh);
    // fill
    ctx.fillStyle = won ? RED : DEEPEST;
    ctx.fillRect(-bw / 2, -bh / 2, bw, bh);
    // border
    ctx.lineWidth = 3;
    ctx.strokeStyle = DEEPEST;
    ctx.strokeRect(-bw / 2, -bh / 2, bw, bh);
    // text
    ctx.fillStyle = "#ffffff";
    ctx.fillText(bannerText, 0, 2);
    ctx.restore();
    ctx.textBaseline = "alphabetic";

    // Username chip top-right
    ctx.font = "13px 'Special Elite', monospace";
    ctx.textAlign = "left";
    const uw = ctx.measureText(username).width + 24;
    const uh = 26;
    const ux = W - uw - 22;
    const uy = 18;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(ux, uy, uw, uh);
    ctx.lineWidth = 3;
    ctx.strokeStyle = DEEPEST;
    ctx.strokeRect(ux + 1.5, uy + 1.5, uw - 3, uh - 3);
    ctx.fillStyle = DEEPEST;
    ctx.fillText(username, ux + 12, uy + 18);

    // Red footer strip
    const footerH = 34;
    ctx.fillStyle = RED;
    ctx.fillRect(0, H - footerH, W, footerH);
    ctx.fillStyle = DEEPEST;
    ctx.fillRect(0, H - footerH, W, 3);
    ctx.fillStyle = "#ffffff";
    ctx.font = "12px 'Special Elite', monospace";
    ctx.textAlign = "center";
    ctx.fillText("@fantastic.noodles  ·  fantasticsnacks.com.au", W / 2, H - footerH / 2 + 4);

    return canvas;
  };

  const generateBlob = async () => {
    const canvas = await renderToCanvas();
    if (!canvas) return null;
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
  };

  const downloadPng = (blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `smokehouse-showdown-${username}.png`;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const sharePngFile = async (blob, shareText) => {
    if (!navigator.canShare) return false;
    const file = new File([blob], `smokehouse-showdown-${username}.png`, { type: "image/png" });
    if (!navigator.canShare({ files: [file] })) return false;
    try {
      await navigator.share({
        files: [file],
        title: "Fantastic Smokehouse Showdown",
        text: shareText,
      });
      return true;
    } catch (e) {
      if (e && e.name === "AbortError") return true;
      return false;
    }
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const blob = await generateBlob();
      if (!blob) return;
      const text = won
        ? `I just sauced 'em ${score} in the Fantastic Smokehouse Showdown! Can you out-slurp me?`
        : `I got smoked ${score} by the ${foeLabel}. Reckon you can do better?`;
      const shared = await sharePngFile(blob, text);
      if (!shared) downloadPng(blob);
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const blob = await generateBlob();
      if (!blob) return;
      if (isMobileDevice()) {
        const shared = await sharePngFile(blob, "Save your Smokehouse Showdown");
        if (!shared) downloadPng(blob);
      } else {
        downloadPng(blob);
      }
    } finally {
      setBusy(false);
    }
  };

  // Preview (HTML version — mirrors the canvas output for in-page display).
  const lockupSrc = "/brand/lockup.png";
  const previewStats = [
    { label: "PARTNER", value: TEAMS[playerTeam].label.replace("Team ", ""), valueSize: 18 },
    { label: "FOE", value: foeLabel, valueSize: 12 },
    { label: "SCORE", value: score, valueSize: 22 },
    { label: "BEST", value: bestMs ? `${bestMs}ms` : "—", valueSize: 18 },
    { label: "AVG", value: avgMs ? `${avgMs}ms` : "—", valueSize: 18 },
  ];

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <div
        ref={wrapperRef}
        className="w-full"
        style={{
          maxWidth: 760,
          margin: "0 auto",
          position: "relative",
          aspectRatio: "760 / 506",
        }}
      >
        <div
          ref={previewRef}
          data-testid="victory-card"
          className="bg-yellow-brand"
          style={{
            width: 760,
            height: 506,
            border: "4px solid var(--deepest-brown)",
            boxShadow: "10px 10px 0 0 rgba(58,29,4,0.45)",
            position: "absolute",
            top: 0,
            left: 0,
            transformOrigin: "top left",
            transform: `scale(${scale})`,
            overflow: "hidden",
            fontFamily: "Anton, sans-serif",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 18,
              right: 22,
              background: "#fff",
              border: "3px solid var(--deepest-brown)",
              padding: "4px 12px",
              fontFamily: "Special Elite, monospace",
              fontSize: 13,
              color: "var(--deepest-brown)",
              zIndex: 4,
            }}
          >
            {username}
          </div>

          <img
            src={lockupSrc}
            alt="Fantastic Smokehouse Showdown"
            style={{
              position: "absolute",
              top: 12,
              left: "50%",
              transform: "translateX(-50%)",
              width: "52%",
              filter: "drop-shadow(4px 5px 0 rgba(58,29,4,0.35))",
              zIndex: 3,
            }}
          />

          <div
            style={{
              position: "absolute",
              top: 230,
              left: "50%",
              transform: "translateX(-50%) rotate(-2deg)",
              background: won ? "var(--red)" : "var(--deepest-brown)",
              color: "#fff",
              padding: "8px 28px",
              border: "3px solid var(--deepest-brown)",
              fontSize: 30,
              letterSpacing: "0.08em",
              boxShadow: "5px 5px 0 0 rgba(58,29,4,0.5)",
              zIndex: 5,
              fontStyle: "italic",
            }}
          >
            {won ? "VICTORY!" : "DEFEATED"}
          </div>

          <img
            src={playerSrc}
            alt={TEAMS[playerTeam].label}
            style={{
              position: "absolute",
              left: 12,
              bottom: 56,
              height: 200,
              zIndex: 2,
              filter: "drop-shadow(4px 5px 0 rgba(58,29,4,0.4))",
            }}
          />
          <img
            src={enemySrc}
            alt={TEAMS[enemyTeam].label}
            style={{
              position: "absolute",
              right: 12,
              bottom: 56,
              height: 200,
              zIndex: 2,
              filter: "drop-shadow(4px 5px 0 rgba(58,29,4,0.4))",
            }}
          />

          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: 92,
              transform: "translateX(-50%)",
              display: "flex",
              gap: 6,
              zIndex: 3,
            }}
          >
            {previewStats.map((s) => (
              <div
                key={s.label}
                style={{
                  background: "#fff",
                  border: "3px solid var(--deepest-brown)",
                  width: 80,
                  height: 72,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  boxShadow: "3px 3px 0 0 rgba(58,29,4,0.45)",
                  padding: 4,
                }}
              >
                <div
                  style={{
                    fontFamily: "Special Elite, monospace",
                    fontSize: 9,
                    color: "var(--dark-brown)",
                    letterSpacing: "0.1em",
                    lineHeight: 1,
                  }}
                >
                  {s.label}
                </div>
                <div
                  style={{
                    fontFamily: "Anton, sans-serif",
                    fontSize: s.valueSize || 18,
                    color: "var(--deepest-brown)",
                    marginTop: 4,
                    lineHeight: 1.05,
                    letterSpacing: "0.02em",
                    wordBreak: "break-word",
                  }}
                >
                  {s.value}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              background: "var(--red)",
              color: "#fff",
              textAlign: "center",
              padding: "10px 0",
              fontFamily: "Special Elite, monospace",
              fontSize: 12,
              letterSpacing: "0.12em",
              borderTop: "3px solid var(--deepest-brown)",
              zIndex: 4,
            }}
          >
            @fantastic.noodles &nbsp;·&nbsp; fantasticsnacks.com.au
          </div>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap justify-center">
        <button
          onClick={handleShare}
          className="btn-logo"
          disabled={busy || !imgs}
          data-testid="share-victory-card"
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", whiteSpace: "nowrap", transform: "skewX(6deg)" }}>
            <Share2 size={18} /> Share your Showdown
          </span>
        </button>
        <button
          onClick={handleDownload}
          className="btn-fantastic yellow"
          disabled={busy || !imgs}
          data-testid="download-victory-card"
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", whiteSpace: "nowrap", transform: "skewX(6deg)" }}>
            <Download size={16} /> {isMobileDevice() ? "Save to Camera Roll" : "Download"}
          </span>
        </button>
      </div>
    </div>
  );
};

export default VictoryCard;
